import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { CREATE_PRICE_TIER_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { PriceTier } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreatePriceTierDialog = ({ onClose, onSuccess, open }: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<PriceTier.CreateRequest>({
        resolver: yupResolver(CREATE_PRICE_TIER_SCHEMA),
        defaultValues: {},
    });
    const mutateCreatePriceTier = useMutation({
        mutationKey: ['priceTier'],
        mutationFn: (data: PriceTier.CreateRequest) =>
            Api.Core.PriceTier.postPriceTier(data),
    });

    const onSubmit = async (values: PriceTier.CreateRequest) => {
        return mutateCreatePriceTier
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating brand!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">Create PriceTier</DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                    <TextFieldElement
                        fullWidth
                        name="displayOrder"
                        label="Display Order"
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 1000,
                                step: 1,
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
